import { Spin } from 'antd'
import { FunctionComponent, useMemo } from 'react'
import styled, { useTheme } from 'styled-components'
import { ApexChartOptions } from '@/components/common/apex-charts'
import Blur from '@/components/common/blur'
import AnalyzeFeedback from '@/components/kol/analyze-feedback'
import KolDetailDataBlock from '@/components/kol/common/kol-detail-data-block'
import Blocker from '@/components/kol/detail/blocker'
import { NoData } from '@/components/kol/detail/no-data'
import ContentDistributionChart from '@/components/kol/detail/overview/content-distribution/content-distribution-chart'
import ContentDistributionTable from '@/components/kol/detail/overview/content-distribution/content-distribution-table'
import useLinkAnalyze from '@/components/kol/hooks/use-link-analyze'
import { mockPlatforms } from '@/constants/mock-data/content-distribution'
import { KolDetailDuration } from '@/hooks/use-authorization/routes'
import useCustomizedTag from '@/hooks/use-customized-tag'
import useIsPublicDetailPage from '@/hooks/use-is-public-detail-page'
import { useIntl } from '@/i18n/hooks/use-intl'
import { PlatformData } from '@/tests/mock-data/mock-content-distribution'
import { FeedbackType } from '@/types/api/feedback'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import { ampli, ClickReportContentTypeAccuracyProperties } from '@/utils/ampli'
import { Platform } from '@/utils/convert-platform'

interface ContentDistributionProps {
  kolInfo?: KolSchemaModel
  isKolInfoLocked: boolean
  showMockData?: boolean
  platform: Platform
  duration: KolDetailDuration
  isFetchingKolInfo: boolean
}

const ContentDistribution: FunctionComponent<ContentDistributionProps> = ({
  kolInfo,
  platform,
  isKolInfoLocked,
  showMockData,
  duration,
  isFetchingKolInfo,
}) => {
  const { formatMessage, formatNumber } = useIntl()
  const theme = useTheme()
  const { getCustomizedTagLocalizedName } = useCustomizedTag()
  const isPublicDetailPage = useIsPublicDetailPage()
  const { linkAnalysis, isFetching: isFetchingLinkAnalyze } = useLinkAnalyze({
    kolId: kolInfo?.id,
    kolDcIdHashed: kolInfo?.dcIdHashed,
  })

  const hiddenData = isKolInfoLocked && !showMockData

  const [series, chartOptions, tableDataList] = useMemo((): [
    number[],
    ApexChartOptions,
    PlatformData[],
  ] => {
    const platformDataList = ((): PlatformData[] => {
      if (showMockData) {
        return mockPlatforms
      }

      const platformDataList =
        linkAnalysis?.find(
          (data) => parseInt(data.platformKey) === platform.type,
        )?.[duration] ?? []

      const concatData = hiddenData
        ? mockPlatforms
        : Array(5).fill({
            customizedTagName: '',
            customizedTagPostCountRatio: 0,
            subCustomizedTags: [],
          })

      return platformDataList
        .concat(concatData)
        .slice(0, 5) // 至少 5 筆資料，不足的以假資料補齊
        .sort((prev, next) =>
          hiddenData
            ? 1
            : next.customizedTagPostCountRatio -
              prev.customizedTagPostCountRatio,
        )
    })()

    const tableDataList = platformDataList.filter(
      (data) => data.customizedTagName !== '',
    )

    const [series, categories] = platformDataList.reduce(
      (acc: [number[], string[]], data) => {
        acc[0].push(data.customizedTagPostCountRatio * 100)
        acc[1].push(getCustomizedTagLocalizedName(data.customizedTagName))
        return acc
      },
      [[], []],
    )

    const chartOptions: ApexChartOptions = {
      chart: {
        id: 'contentDistributionRadarChart',
        height: 260,
        offsetY: -40,
        toolbar: {
          show: false,
        },
      },
      yaxis: {
        min: 0,
        max: Math.ceil(Math.max(...series) / 10) * 10,
        tickAmount: 5,
      },
      markers: { colors: theme.colors.kolrBrand.primary, size: 3 },
      stroke: { colors: [theme.colors.kolrBrand.primary], width: 1.5 },
      fill: {
        opacity: 0.2,
        colors: [theme.colors.kolrBrand.secondary],
      },
      tooltip: {
        y: {
          formatter: (value: number) => {
            return formatNumber(value / 100, {
              isPercentageDisplay: true,
            })
          },
          title: {
            formatter: () =>
              `${formatMessage({
                id: 'kol:section_tag_diagram_percentage',
              })}：`,
          },
        },
      },
      xaxis: {
        categories,
        floating: true,
        position: 'top',
        labels: {
          trim: true,
          hideOverlappingLabels: false,
          style: {
            fontSize: '10px',
            colors: Array(5).fill(theme.colors.base.grey.grey1),
          },
        },
      },
    }

    return [series, chartOptions, tableDataList]
  }, [
    duration,
    formatMessage,
    formatNumber,
    getCustomizedTagLocalizedName,
    hiddenData,
    linkAnalysis,
    platform.type,
    showMockData,
    theme.colors.base.grey.grey1,
    theme.colors.brand.primary,
  ])

  const isFetching = isFetchingLinkAnalyze || isFetchingKolInfo
  const noData = isFetching || series.every((value) => value === 0)

  return (
    <KolDetailDataBlock
      hoverableExtraContent
      colSpan={12}
      header={{
        poweredByAI: true,
        tooltip: {
          title: formatMessage({ id: 'kol:section_tag_distribution_toolip' }),
          placement: 'right',
        },
        title: `${platform?.title} ${formatMessage({
          id: 'kol:content_distribution',
        })}`,
        extraContent: (
          <AnalyzeFeedback
            feedbackEvent={(
              eventProps: ClickReportContentTypeAccuracyProperties,
            ): void => {
              ampli.clickReportContentTypeAccuracy(eventProps)
            }}
            feedbackType={FeedbackType.CustomizedTag}
            kolInfo={kolInfo}
            platform={platform.shortcode}
          />
        ),
      }}
      kolId={kolInfo?.id}
      kolMetrics='Tag Distribution'
      kolName={kolInfo?.name}
      loading={isFetching}
      platform={platform?.shortcode}
    >
      {noData ? (
        <NoData />
      ) : (
        <Wrapper>
          <Spin indicator={<Blocker />} spinning={isPublicDetailPage} />
          <ChartWrapper>
            <Blur activate={hiddenData}>
              <ContentDistributionChart
                chartOptions={chartOptions}
                series={series}
              />
            </Blur>
          </ChartWrapper>
          <ContentDistributionTable
            hiddenData={hiddenData}
            platformDataList={tableDataList}
          />
        </Wrapper>
      )}
    </KolDetailDataBlock>
  )
}

export default ContentDistribution

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const ChartWrapper = styled(Wrapper)`
  width: 100%;
  padding-top: 20px;
  > * {
    flex: 1;
  }
  @media (max-width: 980px) {
    flex-direction: row;
    justify-content: center;
    gap: 40px;
  }
`
